import { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Avatar,
    Chip,
    Paper,
    IconButton,
    Button,
    CircularProgress,
    LinearProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from "@mui/material";
import {
    ArrowForward as BackIcon,
    ExpandMore as ExpandMoreIcon,
    Straighten as MeasurementsIcon,
    MonitorWeight as WeightIcon,
    CameraAlt as PhotoIcon,
    Description as FormIcon,
    CheckCircle as CompletedIcon,
    RadioButtonUnchecked as PendingIcon
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { getCheckInById, markCheckInReviewed } from "../services/CheckInService";
import { useMessageContext } from "../contexts/MessageContext";
import { RTL } from "../cache/CacheRtl";
import { imageUrl } from "../utils/imageUrl";
import {
    BodyWeightSection,
    MeasurementsSection,
    PhotosSection,
    FormSection
} from "../componenets/check-ins/CheckInDetailSections";

const FONT = '"Heebo", "Roboto", sans-serif';

const ITEM_TYPE_CONFIG = {
    BODY_MEASUREMENT: { label: "מדידות גוף", icon: <MeasurementsIcon />, color: "#8B5CF6" },
    BODY_WEIGHT: { label: "משקל גוף", icon: <WeightIcon />, color: "#3B82F6" },
    PROGRESS_PHOTO: { label: "תמונות התקדמות", icon: <PhotoIcon />, color: "#10B981" },
    FORM: { label: "טופס", icon: <FormIcon />, color: "#F59E0B" }
};

const STATUS_CONFIG = {
    PENDING: { label: "ממתין למילוי", color: "#F59E0B", bgColor: "#FEF3C7" },
    COMPLETED: { label: "הושלם", color: "#10B981", bgColor: "#D1FAE5" },
    REVIEWED: { label: "נבדק", color: "#64748B", bgColor: "#F1F5F9" },
    CANCELED: { label: "בוטל", color: "#EF4444", bgColor: "#FEE2E2" }
};

const formatDate = (epochSeconds) => {
    if (!epochSeconds) return null;
    return new Date(epochSeconds * 1000).toLocaleDateString("he-IL");
};

const CheckInDetailPage = () => {
    const { checkInId } = useParams();
    const navigate = useNavigate();
    const { notificationHandler } = useMessageContext();

    const [checkIn, setCheckIn] = useState(null);
    const [loading, setLoading] = useState(true);
    const [reviewing, setReviewing] = useState(false);

    useEffect(() => {
        if (checkInId) {
            setLoading(true);
            getCheckInById(checkInId)
                .then(res => setCheckIn(res.data))
                .catch(() => setCheckIn(null))
                .finally(() => setLoading(false));
        }
    }, [checkInId]);

    const handleReview = async () => {
        setReviewing(true);
        try {
            await markCheckInReviewed(checkInId);
            setCheckIn(prev => prev ? { ...prev, status: "REVIEWED", reviewedDate: Date.now() / 1000 } : prev);
            notificationHandler.success("צ'ק-אין סומן כנבדק");
        } catch {
            notificationHandler.error("שגיאה בסימון הצ'ק-אין");
        } finally {
            setReviewing(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
                <CircularProgress sx={{ color: "#57837E" }} />
            </Box>
        );
    }

    if (!checkIn) {
        return (
            <RTL>
                <Box sx={{ px: 3, pt: 3 }}>
                    <IconButton onClick={() => navigate("/app/check-ins")} sx={{ color: "#64748B" }}>
                        <BackIcon />
                    </IconButton>
                    <Typography sx={{ textAlign: "center", color: "#64748B", py: 8, fontFamily: FONT }}>
                        לא ניתן לטעון את הצ'ק-אין
                    </Typography>
                </Box>
            </RTL>
        );
    }

    const statusConfig = STATUS_CONFIG[checkIn.status] || STATUS_CONFIG.PENDING;
    const completedCount = checkIn.items?.filter(i => i.completed).length || 0;
    const totalCount = checkIn.items?.length || 0;
    const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

    const renderItemSection = (item) => {
        switch (item.type) {
            case "BODY_WEIGHT":
                return <BodyWeightSection item={item} />;
            case "BODY_MEASUREMENT":
                return <MeasurementsSection traineeId={checkIn.traineeId} item={item} />;
            case "PROGRESS_PHOTO":
                return <PhotosSection traineeId={checkIn.traineeId} item={item} />;
            case "FORM":
                return <FormSection item={item} />;
            default:
                return null;
        }
    };

    return (
        <RTL>
            <Box sx={{ pb: 4 }}>
                {/* Header */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: { xs: 2, md: 3 }, pt: 2, mb: 2 }}>
                    <IconButton onClick={() => navigate("/app/check-ins")} sx={{ color: "#64748B" }}>
                        <BackIcon />
                    </IconButton>
                    <Typography sx={{ fontSize: "20px", fontWeight: 600, color: "#1E293B", fontFamily: FONT }}>
                        פרטי צ'ק-אין
                    </Typography>
                </Box>

                {/* Trainee Info Bar */}
                <Paper
                    elevation={0}
                    sx={{
                        mx: { xs: 2, md: 3 },
                        mb: 2,
                        padding: "16px 20px",
                        borderRadius: "12px",
                        border: "1px solid #E2E8F0",
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 2
                    }}
                >
                    <Avatar
                        src={imageUrl(checkIn.traineePhotoUrl, 44)}
                        sx={{ width: 44, height: 44, fontSize: "18px", backgroundColor: "#57837E" }}
                    >
                        {checkIn.traineeName?.[0]}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: "16px", fontWeight: 600, color: "#1E293B", fontFamily: FONT }}>
                            {checkIn.traineeName}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 0.5, flexWrap: "wrap" }}>
                            <Chip
                                label={statusConfig.label}
                                size="small"
                                sx={{
                                    backgroundColor: statusConfig.bgColor,
                                    color: statusConfig.color,
                                    fontWeight: 600,
                                    fontSize: "12px",
                                    height: "24px"
                                }}
                            />
                            <Typography sx={{ fontSize: "13px", color: "#64748B", fontFamily: FONT }}>
                                נשלח: {formatDate(checkIn.createdDate)}
                            </Typography>
                            {checkIn.completedDate && (
                                <Typography sx={{ fontSize: "13px", color: "#64748B", fontFamily: FONT }}>
                                    הושלם: {formatDate(checkIn.completedDate)}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Paper>

                {/* Progress Bar */}
                <Paper
                    elevation={0}
                    sx={{
                        mx: { xs: 2, md: 3 },
                        mb: 3,
                        padding: "14px 20px",
                        borderRadius: "12px",
                        border: "1px solid #E2E8F0"
                    }}
                >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                        <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1E293B", fontFamily: FONT }}>
                            התקדמות: {completedCount} מתוך {totalCount} הושלמו
                        </Typography>
                        <Typography sx={{ fontSize: "13px", color: "#64748B", fontFamily: FONT }}>
                            {Math.round(progressPercent)}%
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={progressPercent}
                        sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: "#E2E8F0",
                            "& .MuiLinearProgress-bar": {
                                borderRadius: 4,
                                backgroundColor: "#57837E"
                            }
                        }}
                    />
                </Paper>

                {/* Item Sections */}
                <Box sx={{ mx: { xs: 2, md: 3 }, display: "flex", flexDirection: "column", gap: 2 }}>
                    {checkIn.items?.map((item) => {
                        const config = ITEM_TYPE_CONFIG[item.type] || {};
                        return (
                            <Accordion
                                key={item.id}
                                defaultExpanded={item.completed}
                                elevation={0}
                                sx={{
                                    border: "1px solid #E2E8F0",
                                    borderRadius: "12px !important",
                                    "&:before": { display: "none" },
                                    overflow: "hidden"
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                        px: 2.5,
                                        "& .MuiAccordionSummary-content": {
                                            alignItems: "center",
                                            gap: 1.5,
                                            my: 1
                                        }
                                    }}
                                >
                                    {item.completed
                                        ? <CompletedIcon sx={{ color: "#10B981", fontSize: "22px" }} />
                                        : <PendingIcon sx={{ color: "#CBD5E1", fontSize: "22px" }} />
                                    }
                                    <Box sx={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: "8px",
                                        backgroundColor: `${config.color}15`,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                        "& svg": { color: config.color, fontSize: "20px" }
                                    }}>
                                        {config.icon}
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1E293B", fontFamily: FONT }}>
                                            {config.label}
                                            {item.formTemplateName && ` — ${item.formTemplateName}`}
                                        </Typography>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.25 }}>
                                            {item.required && (
                                                <Chip label="חובה" size="small" sx={{
                                                    backgroundColor: "#FEE2E2", color: "#DC2626",
                                                    fontSize: "11px", height: "20px", fontWeight: 600
                                                }} />
                                            )}
                                            {item.completed && item.completedDate && (
                                                <Typography sx={{ fontSize: "12px", color: "#64748B", fontFamily: FONT }}>
                                                    הושלם: {formatDate(item.completedDate)}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0, borderTop: "1px solid #F1F5F9" }}>
                                    {renderItemSection(item)}
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
                </Box>

                {/* Review Button */}
                {checkIn.status === "COMPLETED" && (
                    <Box sx={{
                        mx: { xs: 2, md: 3 },
                        mt: 3,
                        display: "flex",
                        justifyContent: "center"
                    }}>
                        <Button
                            onClick={handleReview}
                            variant="contained"
                            disabled={reviewing}
                            sx={{
                                minWidth: "220px",
                                height: "48px",
                                borderRadius: "10px",
                                backgroundColor: "#57837E",
                                fontSize: "15px",
                                fontWeight: 600,
                                fontFamily: FONT,
                                "&:hover": { backgroundColor: "#46706B" }
                            }}
                        >
                            {reviewing ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : "סמן כנבדק"}
                        </Button>
                    </Box>
                )}
            </Box>
        </RTL>
    );
};

export default CheckInDetailPage;
